import { useMemo, useState } from "react";
import {
  BBS_CATEGORY_IDS,
  VISIBILITY,
  addComment,
  canSeeContent,
  getBlurbs,
  getVisibility,
  hasLiked,
  likesForPost,
  listComments,
  listPosts,
  savePost,
  toggleLike,
} from "@hub";
import { useHubVisitor } from "../hubContext";
import { appUrl } from "../config";

export default function Board() {
  const visitor = useHubVisitor();
  const [pulse, setPulse] = useState(0);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("desc");
  const [draft, setDraft] = useState({ title: "", body: "", category: "meetings", visibility: "public" });
  const [comments, setComments] = useState({});
  const visibility = getVisibility();
  const blurbs = getBlurbs();

  const posts = useMemo(() => {
    const role = visitor.role || "";
    return listPosts()
      .filter((item) => canSeeContent(item.visibility, role))
      .filter((item) => !category || item.category === category)
      .filter((item) => {
        const hay = `${item.title} ${item.body}`.toLowerCase();
        return !query || hay.includes(query.toLowerCase());
      })
      .sort((a, b) =>
        sort === "asc"
          ? String(a.created_at).localeCompare(String(b.created_at))
          : String(b.created_at).localeCompare(String(a.created_at))
      );
  }, [visitor.role, category, query, sort, pulse]);

  if (visibility.board === false) {
    return (
      <article data-tour="community-page">
        <h1>Community</h1>
        <p className="lead">The board is hidden for now.</p>
      </article>
    );
  }

  function refresh() {
    setPulse((n) => n + 1);
  }

  return (
    <article data-tour="community-page">
      <h1>Community board</h1>
      <p className="lead">{blurbs.board}</p>
      {visitor.role === "agent" || visitor.role === "broker" ? (
        <p>
          This board is the public Journey community. The Hub — Convention Hall — is the private
          professional space on your desk.{" "}
          <a href={appUrl(`/${visitor.role}/hub/hall`)}>Open The Hub</a>
        </p>
      ) : (
        <p className="muted">
          Public visitors see public posts only. Brokers and agents keep a private Hub on the desk.
        </p>
      )}

      <div className="hub-filters">
        <label>
          Search
          <input value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <label>
          Category
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">All</option>
            {BBS_CATEGORY_IDS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="desc">Newest</option>
            <option value="asc">Oldest</option>
          </select>
        </label>
      </div>

      {visitor.signedIn ? (
        <section className="card" style={{ marginBottom: "1rem" }}>
          <h2>Post a note</h2>
          <label className="field">
            <span>Title</span>
            <input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
          </label>
          <label className="field">
            <span>Body</span>
            <textarea rows={3} value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
          </label>
          <div className="hub-filters">
            <label>
              Category
              <select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })}>
                {BBS_CATEGORY_IDS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Visibility
              <select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value })}>
                {VISIBILITY.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              if (!draft.title.trim()) return;
              savePost({
                ...draft,
                author_name: visitor.user || visitor.role || "Guest",
                author_role: visitor.role,
              });
              setDraft({ title: "", body: "", category: "meetings", visibility: "public" });
              refresh();
            }}
          >
            Post
          </button>
        </section>
      ) : (
        <p className="muted">Unsigned visitors see public posts only. Sign in through the app footer Support link to post.</p>
      )}

      {posts.map((post) => {
        const userKey = visitor.user || visitor.role || "";
        const replies = listComments(post.id);
        return (
          <article key={post.id} className={`card hub-post${post.flagged ? " is-flagged" : ""}`}>
            <p className="muted">
              {BBS_CATEGORY_IDS.find((item) => item.id === post.category)?.label || post.category} · {post.visibility} ·{" "}
              {post.author_name}
              {post.flagged ? " · flagged" : ""}
            </p>
            <h2>{post.title}</h2>
            <p>{post.body}</p>
            <p>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  toggleLike(post.id, userKey || "guest");
                  refresh();
                }}
              >
                Like · {likesForPost(post.id)}
                {hasLiked(post.id, userKey) ? " (yours)" : ""}
              </button>
              {visitor.isAdmin ? (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    savePost({ ...post, flagged: !post.flagged });
                    refresh();
                  }}
                >
                  {post.flagged ? "Clear flag" : "Flag"}
                </button>
              ) : null}
            </p>
            <ul className="hub-comments">
              {replies.map((item) => (
                <li key={item.id}>
                  <strong>{item.author_name}</strong> {item.body}
                </li>
              ))}
            </ul>
            {visitor.signedIn ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const body = comments[post.id] || "";
                  if (!body.trim()) return;
                  addComment({
                    post_id: post.id,
                    body,
                    author_name: visitor.user || visitor.role,
                    author_role: visitor.role,
                  });
                  setComments((current) => ({ ...current, [post.id]: "" }));
                  refresh();
                }}
              >
                <label className="field">
                  <span>Reply</span>
                  <input
                    value={comments[post.id] || ""}
                    onChange={(event) => setComments((current) => ({ ...current, [post.id]: event.target.value }))}
                  />
                </label>
                <button type="submit" className="btn">
                  Reply
                </button>
              </form>
            ) : null}
          </article>
        );
      })}
      {!posts.length ? <p className="muted">No posts match this view.</p> : null}
    </article>
  );
}
